import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router'
import { useShallow } from 'zustand/react/shallow'

import { NodeType } from '@ui/__generated__/graphql'
import { Button } from '@ui/components/core'
import { DBLTable, TelemetrySearchBar } from '@ui/components/elements'
import { PinIcon } from '@ui/components/icons'
import {
  AttributeKey,
  DatableDataType,
  DatableLog,
  DatableTrace,
  StartAndEnd,
  TelemetryData,
  TelemetryEvent,
} from '@ui/interfaces'
import { getStartAndEnd } from '@ui/lib/helpers/timeWindow'
import useAppNavigation from '@ui/lib/hooks/useAppNavigation'
import { useAppSearchParams } from '@ui/lib/hooks/useAppSearchParams'
import { useSetupNodesEdges } from '@ui/lib/hooks/useSetupNodesEdges'
import { getInAbsoluteTime } from '@ui/lib/utils'
import { TelemetryQuery, TopValuesQuery } from '@ui/loaders/telemetry'
import { GetNodesAndEdgesLazyQuery } from '@ui/loaders/workflow'
import { PipelineMiniMap } from '@ui/pages/explore/components/PipelineMiniMap'
import { Visualization } from '@ui/pages/explore/components/Visualization'
import { FeatureFlagName, useFeatureFlag } from '@ui/providers/FeatureFlagProvider'
import { RouteNamesEnum } from '@ui/router/routes'
import { useNodeAndEdgeStore, usePipelinesStore, useTelemetryStore, useTimeStore } from '@ui/stores'
import { useClickAway } from '@uidotdev/usehooks'
import clsx from 'clsx'

import { GroupBySelector } from './components/Visualization/GroupBySelector'

const PAGE_SIZE = 100

export const EventsExplorerV2 = () => {
  const { instanceId } = useParams<{ instanceId: string }>()

  const workflowUpdateEnabled = useFeatureFlag(FeatureFlagName.WorkflowUpdate)
  const queryFirehoseNodeDirectlyEnabled = useFeatureFlag(FeatureFlagName.SupportQueryingFirehoseNodeDirectly)

  const { navigateTo } = useAppNavigation()
  const { searchParams } = useAppSearchParams()
  const pipelineId = useMemo(() => searchParams?.get('pipelineId'), [searchParams])

  const containerRef = useRef<HTMLDivElement>(null)
  const visualizationParentRef = useRef<HTMLDivElement>(null)
  const fetchedInitalKey = useRef<boolean>(false)
  const dataSourceRef = useRef<string>('logs')

  const groupBySelectorRef = useClickAway(() => setShowAttributeSelector(false))

  const prevTelemetryDataRef = useRef<TelemetryData | undefined>()

  const {
    selectedKey,
    setSelectedKey,
    dataSource,
    whereClause,
    setWhereClause,
    setLogs,
    setSpans,
    setUniqueKeysLogs,
    setUniqueKeysSpans,
    uniqueKeysLogs,
    uniqueKeysSpans,
    setPinnedSampleEvents,
  } = useTelemetryStore(
    useShallow((state) => ({
      setSelectedKey: state.setSelectedKey,
      selectedKey: state.selectedKey,
      dataSource: state.dataSource,
      setDataSource: state.setDataSource,
      whereClause: state.whereClause,
      setWhereClause: state.setWhereClause,
      setLogs: state.setLogs,
      setSpans: state.setSpans,
      setUniqueKeysLogs: state.setUniqueKeysLogs,
      setUniqueKeysSpans: state.setUniqueKeysSpans,
      uniqueKeysLogs: state.uniqueKeysLogs,
      uniqueKeysSpans: state.uniqueKeysSpans,
      setPinnedSampleEvents: state.setPinnedSampleEvents,
    }))
  )

  const timewindow = useTimeStore(useShallow((state) => state.timewindow))

  const { selectPipeline, selectedPipeline, pipelines } = usePipelinesStore(
    useShallow((state) => ({
      selectedPipeline: state.selectedPipeline,
      selectPipeline: state.selectPipeline,
      pipelines: state.pipelines,
    }))
  )

  const { currentNodeId, setCurrentNodeId, setRefreshLayout } = useNodeAndEdgeStore(
    useShallow((state) => ({
      currentNodeId: state.currentNodeId,
      setCurrentNodeId: state.setCurrentNodeId,
      setRefreshLayout: state.setRefreshLayout,
    }))
  )

  const [paginatedTelemetryEvents, setPaginatedTelemetryEvents] = useState<
    Record<number, TelemetryEvent<DatableLog | DatableTrace>>
  >({})

  const [offset, setOffset] = useState(0)
  const [isFetchingMore, setIsFetchingMore] = useState(false)
  const [startAndEnd, setStartAndEnd] = useState<StartAndEnd>(getStartAndEnd(timewindow))
  const [hasValidNodeId, setHasValidNodeId] = useState(false)
  const [loadingNodesAndEdges, setLoadingNodesAndEdges] = useState(false)
  const [showAttributeSelector, setShowAttributeSelector] = useState<boolean>(false)

  const { startTime, endTime } = getInAbsoluteTime(timewindow)
  const tableData = useMemo(() => Object.values(paginatedTelemetryEvents).flat(), [paginatedTelemetryEvents])

  const {
    data: telemetryQueryData,
    loading: telemetryQueryLoading,
    error: telemetryQueryError,
    fetchMore,
  } = TelemetryQuery({
    from: dataSource,
    where: whereClause,
    limit: PAGE_SIZE,
    offset,
    nodeId: currentNodeId,
    ...startAndEnd,
    skip: !hasValidNodeId,
  })

  const {
    data: topValuesQueryData,
    loading: topValuesLoading,
    error: topValuesQueryError,
  } = TopValuesQuery({
    key: selectedKey.key,
    from: dataSource,
    where: whereClause,
    nodeId: currentNodeId,
    ...startAndEnd,
    skip: !hasValidNodeId,
  })

  const [getNodesAndEdges] = GetNodesAndEdgesLazyQuery()

  const onFetchMore = async () => {
    const newOffset = offset + PAGE_SIZE
    setIsFetchingMore(true)

    try {
      await fetchMore({
        variables: {
          offset: newOffset,
        },
      })
      setOffset(newOffset)
    } finally {
      setIsFetchingMore(false)
    }
  }

  const handlePinToLiveSample = () => {
    setPinnedSampleEvents(true)
    navigateTo(RouteNamesEnum.EXISTING_PIPELINE, {
      pipelineId: selectedPipeline?.id,
      instanceId: instanceId,
    })
  }

  const setEvents = useCallback(
    (dataSource: DatableDataType, events: TelemetryEvent<DatableLog | DatableTrace>[]) => {
      if (dataSource === 'logs') {
        setLogs(events as TelemetryEvent<DatableLog>[])
      } else {
        setSpans(events as TelemetryEvent<DatableTrace>[])
      }
    },
    [setLogs, setSpans]
  )

  const primaryKeys = useMemo(() => {
    return dataSource === 'logs'
      ? ['resource[service.name]', 'resource[host.name]', 'body']
      : ['resource[service.name]', 'resource[host.name]', 'name']
  }, [dataSource])

  const selectFromPrimaryKey = useCallback(
    (data: TelemetryData) => {
      // Find the first matching key in the prioritized list
      const key = primaryKeys
        .map((keyName) => data.telemetry?.uniqueKeys?.find((key: AttributeKey) => key.key === keyName))
        .find((foundKey) => foundKey !== undefined)

      if (key) {
        setSelectedKey(key)
      }
    },
    [primaryKeys, setSelectedKey]
  )

  useEffect(() => {
    if (telemetryQueryData) {
      if (prevTelemetryDataRef.current !== telemetryQueryData) {
        // Update telemetry data
        prevTelemetryDataRef.current = telemetryQueryData
        // Set telemetry data
        setEvents(dataSource, telemetryQueryData.telemetry.events)

        // Store paginated telemetry events
        const page = offset / PAGE_SIZE
        setPaginatedTelemetryEvents((prev) => ({
          ...prev,
          [page]: telemetryQueryData.telemetry.events || [],
        }))

        //   Select primary key on first load or data source change
        if (!fetchedInitalKey.current) {
          selectFromPrimaryKey(telemetryQueryData)
          fetchedInitalKey.current = true
        } else if (dataSourceRef.current !== dataSource) {
          selectFromPrimaryKey(telemetryQueryData)
          dataSourceRef.current = dataSource
        }

        if (dataSource === 'logs') {
          setUniqueKeysLogs(telemetryQueryData.telemetry?.uniqueKeys as AttributeKey[])
        } else {
          setUniqueKeysSpans(telemetryQueryData.telemetry?.uniqueKeys as AttributeKey[])
        }
      }
    }
  }, [
    dataSource,
    endTime,
    offset,
    selectFromPrimaryKey,
    setEvents,
    setUniqueKeysLogs,
    setUniqueKeysSpans,
    setWhereClause,
    startTime,
    telemetryQueryData,
    currentNodeId,
    timewindow,
  ])

  // Select pipeline on first load or change for minimap
  const { setupPipelineWorkflow } = useSetupNodesEdges({
    isNew: false,
    withSteps: workflowUpdateEnabled,
  })

  // Select pipeline based on search params
  // We want to select the source node when we navigate to the events explorer
  // to display the data in the table
  useEffect(() => {
    if (!selectedPipeline && pipelines.length > 0 && !pipelineId) {
      selectPipeline(pipelines[0])
    }

    if (pipelineId) {
      const pipeline = pipelines.find((pipeline) => pipeline.id === pipelineId)

      if (pipeline) {
        selectPipeline(pipeline)
      }
    }
  }, [pipelineId, pipelines, selectPipeline])

  useEffect(() => {
    // Otherwise, if we have a selected pipeline, setup the pipeline workflow
    if (!loadingNodesAndEdges && selectedPipeline) {
      setLoadingNodesAndEdges(true)
      // updateSearchParams({ pipelineId: selectedPipeline.id })

      // Current all pipelineworkflow query doens't return steps so we need to fetch the nodes and edges separately
      // don't want to make a change to query and have to force a bump
      getNodesAndEdges({
        variables: { pipelineId: selectedPipeline.id },
        onCompleted: (data) => {
          const pipelineWithCorrectNodesAndEdges = {
            ...selectedPipeline,
            nodes: data?.pipelineWorkflow.nodes,
            edges: data?.pipelineWorkflow.edges,
          }
          setupPipelineWorkflow(pipelineWithCorrectNodesAndEdges, true)
          setLoadingNodesAndEdges(false)
          setRefreshLayout(true)
        },
        onError: () => {
          console.error('Error fetching nodes and edges')
          setLoadingNodesAndEdges(false)
        },
      })

      // Older versions of the backend don't support querying the firehose node directly
      // so we need to query the node by its id if the feature flag is enabled
      if (queryFirehoseNodeDirectlyEnabled) {
        const firehoseNode = selectedPipeline.nodes?.find((node) => node?.type === NodeType.Firehose)
        setCurrentNodeId(firehoseNode?.id ?? null)
      } else {
        setCurrentNodeId(null)
      }

      setHasValidNodeId(true)
    }
  }, [
    pipelines,
    queryFirehoseNodeDirectlyEnabled,
    selectPipeline,
    selectedPipeline,
    setCurrentNodeId,
    setupPipelineWorkflow,
    getNodesAndEdges,
  ])

  useEffect(() => {
    // Reset pagination and start/end time when filters change
    return () => {
      if (!telemetryQueryLoading) {
        setOffset(0)
        setPaginatedTelemetryEvents({})
        const startAndEnd = getStartAndEnd(timewindow)
        setStartAndEnd(startAndEnd)
      }
    }
  }, [dataSource, timewindow, whereClause, currentNodeId])

  // If error, throw error
  useEffect(() => {
    if (telemetryQueryError || topValuesQueryError) {
      const combinedError = [telemetryQueryError?.message, topValuesQueryError?.message].filter(Boolean).join('; ')

      throw new Error(combinedError)
    }
  }, [telemetryQueryError, topValuesQueryError])

  return (
    <div
      className="border-primary size-full flex flex-col space-y-2.5"
      data-testid="events-explorer"
      ref={containerRef}>
      <TelemetrySearchBar error={telemetryQueryError} />
      <div className="relative flex h-[calc(100dvh-10rem)] flex-row space-x-2.5">
        {/* Pipeline Mini Map */}
        <div
          style={{
            height: `${
              containerRef?.current?.clientHeight ? containerRef.current.clientHeight * 0.5 : 0
            }                                                                           `,
          }}
          className="h-full w-4/12 rounded-md border border-gray-200">
          {!selectedPipeline && !pipelines[0] && (
            <div className="size-full absolute z-10 flex items-center justify-center rounded-md backdrop-blur-lg">
              <div className="flex w-1/2 flex-col items-center text-center">
                Create a new pipeline or select an existing pipeline to view data
                <Button variant="primary" onClick={() => navigateTo(RouteNamesEnum.NEW_PIPELINE)}>
                  Create Pipeline
                </Button>
              </div>
            </div>
          )}

          <PipelineMiniMap selectedPipeline={selectedPipeline} loading={loadingNodesAndEdges} />
        </div>

        <div className="flex h-full w-8/12 flex-col space-y-2.5 rounded-md">
          {/* Visualization */}
          <div className="h-full w-full rounded-md border border-gray-200 bg-white" ref={visualizationParentRef}>
            <div className="w-full border-b border-gray-200">
              <div className="flex flex-row items-center justify-between p-3">
                <p className="text-sm font-semibold">Visualize your data</p>

                <div className="relative">
                  <Button
                    size="small"
                    variant="secondary"
                    className={clsx('border-gray-900 group-hover:bg-gray-900', showAttributeSelector && 'bg-gray-900')}
                    onClick={() => setShowAttributeSelector(!showAttributeSelector)}>
                    Select Attribute
                  </Button>
                  {showAttributeSelector && (
                    <div
                      className="absolute right-0 top-10 z-40 max-h-96 overflow-y-auto rounded-md border border-gray-200 bg-white"
                      ref={groupBySelectorRef as React.RefObject<HTMLDivElement>}>
                      <GroupBySelector
                        data={prevTelemetryDataRef.current}
                        handleClose={() => setShowAttributeSelector(false)}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex h-52 w-full p-3">
              <Visualization data={topValuesQueryData} loading={topValuesLoading} parentRef={visualizationParentRef} />
            </div>
          </div>

          {/* Table */}
          {/* <div className="flex h-20 max-h-[calc(100%-12rem)] min-h-[calc(100%-12rem)] w-full max-w-[calc(100vw-35rem)]"> */}
          <div className="flex h-[calc(100%-17.5rem)] min-h-[calc(100%-17.5rem)] w-full max-w-[calc(100vw-35rem)]">
            <DBLTable
              tableData={tableData}
              loading={!prevTelemetryDataRef.current || telemetryQueryLoading}
              dataSource={dataSource}
              fetchMoreProps={{
                isFetchingMore: isFetchingMore,
                onFetchMore: onFetchMore,
              }}
              defaultColumnOrder={primaryKeys}
              uniqueKeys={dataSource === 'logs' ? uniqueKeysLogs : uniqueKeysSpans}
              detailsModal={true}
              customTableHeaderButton={
                <Button
                  variant="primary"
                  icon={<PinIcon className="size-3 fill-none stroke-white stroke-[1.6]" />}
                  onClick={handlePinToLiveSample}>
                  Pin to live sample and go to Pipeline
                </Button>
              }
            />
          </div>
        </div>
      </div>
    </div>
  )
}
